import { RodeoEventRegistration } from "./rodeoEventRegistration";
import { AccountHelper } from "../lib/accountHelper";

export interface RegistrationChangeRequest {
    id: string;
    registrationId: number;
    registration: RodeoEventRegistration;
    rodeoId: number;
    organizationId?: number | null;
    requestedChangesJson: string;
    status: ChangeRequestStatus;
    staffComments: string;
    staffResponses: string;
    errorMessage: string;
    isTurnoutRequest: boolean;
    turnoutReason?: TurnoutReason;
    turnoutContestant?: TurnoutContestant;
    turnoutDocumentUrl: string;
    contestantComments: string;
    createdOn: string;
    modifiedOn: string | null;
    reviewedOn: string | null;
    completedOn: string | null;
    requestedById: number;
    reviewedById: number | null;

    isStaffResponse: boolean;
    paymentBalanceRequired: PaymentBalanceRequired;
    turnoutOverride?: TurnoutReason;

    documents: RegistrationChangeRequestDocument[];

    // Fees
    turnoutFeeDoctorsRelease: number;
    turnoutFeeVetRelease: number;
    turnoutFeeNotified: number;
    turnoutFeeNonNotified: number;
    turnoutFeeNotifiedPerformance: number; 
    turnoutFeeNonNotifiedPerformance: number;
    turnoutFeeNotifiedNonPreference: number;
    turnoutFeeNonNotifiedNonPreference: number;
    turnoutFeeNotifiedSlack: number;
    turnoutFeeNonNotifiedSlack: number;

    entryCostTurnoutAdjustment: number;
}

export class RegistrationChangeRequestDocument {
    id?: number;
    registrationChangeRequestId?: string;
    url: string;
    createdOn?: Date;
}
  
export class RegistrationChangeRequestReleaseCount {
    organizationId: number;
    doctorReleaseCount: number;
    vetReleaseCount: number;
}

export enum TurnoutReason
{
    Turnout,
    DoctorRelease,
    VetRelease,
    Drawout,
    NotifiedTurnout,
    NonNotifiedTurnout,
    VisibleInjury
}

export enum TurnoutContestant {
    FullEntry,
    Header,
    Heeler
}

export enum ChangeRequestStatus
{
    Pending,
    UnderReview, //if save
    ApprovedPendingPayment,
    Completed,
    Denied,
    Cancelled,
    ErrorApplyingChanges,
    Reversed
}

export enum PaymentBalanceRequired
{
	FullBalance, //remainder of entry costs (and fees if configured for org)
    FeeOnly, //needs to only pay remainder of entry costs
	Refund, //customer will receive a refund - order will be marked completed and refund line items will need to be added to the order
    CancelOrder, //no further payments required, cancel the order.
    FineOnly //needs to only pay Turnout fee costs 
}

export class ChangeRequestHelper {
    static isMoneyOutOfPot(request: RegistrationChangeRequest) {
        return (request.status === ChangeRequestStatus.ApprovedPendingPayment || request.status === ChangeRequestStatus.Completed)
            && (request.turnoutReason === TurnoutReason.DoctorRelease || request.turnoutReason === TurnoutReason.VetRelease || request.turnoutReason === TurnoutReason.Drawout);
    }

    static create(): RegistrationChangeRequest {
        return {
            id: '00000000-0000-0000-0000-000000000000',
            registrationId: 0,
            registration: null,
            rodeoId: 0,
            organizationId: null,
            requestedChangesJson: undefined,
            status: ChangeRequestStatus.ApprovedPendingPayment,
            staffComments: undefined,
            staffResponses: undefined,
            errorMessage: undefined,
            isTurnoutRequest: false,
            turnoutReason: 4, // Notified Turnout
            turnoutContestant: TurnoutContestant.FullEntry,
            turnoutDocumentUrl: undefined,
            contestantComments: undefined,
            createdOn: new Date().toISOString(),
            modifiedOn: null,
            reviewedOn: new Date().toISOString(),
            completedOn: null,
            requestedById: 0,
            reviewedById: AccountHelper.instance().token.id,
            isStaffResponse: true,
            paymentBalanceRequired: PaymentBalanceRequired.FullBalance, // Assuming you have this enum defined
            turnoutOverride: undefined,
            documents: [],
            turnoutFeeDoctorsRelease: 0,
            turnoutFeeVetRelease: 0,
            turnoutFeeNotified: 0,
            turnoutFeeNonNotified: 0,
            turnoutFeeNotifiedPerformance: 0, 
            turnoutFeeNonNotifiedPerformance: 0,
            turnoutFeeNotifiedNonPreference: 0,
            turnoutFeeNonNotifiedNonPreference: 0,
            turnoutFeeNotifiedSlack: 0,
            turnoutFeeNonNotifiedSlack: 0,
            entryCostTurnoutAdjustment: 0
        };
    }
    
    static update(data): RegistrationChangeRequest {

        let updatedRequest = ChangeRequestHelper.create();

        updatedRequest.id = data.id;
        updatedRequest.rodeoId = data.rodeoId;
        updatedRequest.organizationId = data.organizationId;
        updatedRequest.registrationId = data.registrationId;
        updatedRequest.requestedChangesJson = data.requestedChangesJson;
        updatedRequest.status = data.status;
        updatedRequest.contestantComments = data.contestantComments;
        updatedRequest.staffComments = data.staffComments;
        updatedRequest.staffResponses = data.staffResponses;
        updatedRequest.errorMessage = data.errorMessage;
        updatedRequest.isTurnoutRequest = data.isTurnoutRequest;
        updatedRequest.isStaffResponse = true;
        updatedRequest.turnoutReason = data.turnoutReason;
        updatedRequest.turnoutOverride = data.turnoutOverride;
        updatedRequest.turnoutContestant = data.turnoutContestant;
        updatedRequest.paymentBalanceRequired = data.paymentBalanceRequired;
        updatedRequest.turnoutDocumentUrl = data.turnoutDocumentUrl;
        updatedRequest.documents = data.documents;
        updatedRequest.createdOn = data.createdOn;
        updatedRequest.modifiedOn = new Date().toISOString().slice(0,22);
        updatedRequest.reviewedOn = data.reviewedOn;
        updatedRequest.requestedById = data.requestedById;
        updatedRequest.reviewedById = data.reviewedById;
        updatedRequest.completedOn = data.completedOn;

        return updatedRequest;
    }

    static getReasonAbbreviation(request: RegistrationChangeRequest): string {
        const turnoutReason = request.turnoutOverride ? request.turnoutOverride : request.turnoutReason;

        switch (turnoutReason) {
            case TurnoutReason.Drawout:
                return "DO";
            case TurnoutReason.DoctorRelease:
                return "DR";
            case TurnoutReason.VetRelease:
                return "VR";
            case TurnoutReason.NonNotifiedTurnout:
                return "NNTO";
            case TurnoutReason.VisibleInjury:
                return "VI";
            default:
                return "TO";
        }
    }
}


// WEBPACK FOOTER //
// ./src/model/registration-change-request.ts